/* sb-dc-imgshrink.js — 2026-09-16. Shrink big customer photos in the browser before the editor takes them.

   Why: the Image panel (bundle) refuses anything over 20MB with one red line inside the panel, so a 40MB
   photo never appears and the customer thinks the upload is "slow". Under 20MB the full-size file goes
   into the design as a data URL: a 4MB JPEG becomes 5.6MB of design JSON, which blocks the local autosave
   (2.5MB → no "Pick up"), inflates the order JSON (dropped silently over 9.5MB) and memory.
   The print file is at most 12,000px on the long side (~114dpi on 8ft), so pixels beyond that are thrown
   away at print anyway. Same idea as the Step & Repeat panel's shrink (sb-dc-steprepeat.js), applied to
   the two general paths: the Image panel's <input type=file> (also used by Replace logo) and drag & drop
   (sb-dc-aug.js handleFiles → window.__dcShrinkFiles).

   Rule: raster only (PNG/JPEG/WEBP/BMP; SVG and GIF untouched). Files over 1.5MB are decoded and
   re-encoded: at most 12 million pixels (Safari's canvas limit is 16M) and 8,000px on the long side.
   Transparent → PNG, opaque → JPEG 0.85 (0.8 if the result is still over 1.8MB). Never returns a bigger file.
   Anything that fails (decode error, old browser without DataTransfer) falls back to the original file, so
   the editor behaves exactly as before. window.__dcNoShrink = true skips it (tests). */
(function () {
  'use strict';
  var AREA = 12e6, LONG = 8000, BYTES = 1.5 * 1024 * 1024, Q = 0.85, Q2 = 0.8, RETRY_OVER = 1.8 * 1024 * 1024;   // 12MP ≈ 4480×2678: ~47ppi on an 8ft banner, ~1.2–1.8MB JPEG in Chrome (Safari encodes ~1.4× larger); under 1.8MB the local autosave (2.5MB of base64) still works

  function isRaster(f) {
    var t = f.type || '', n = f.name || '';
    if (/svg|gif/i.test(t) || /\.(svg|gif)$/i.test(n)) return false;
    return /^image\/(png|jpe?g|webp|bmp)/i.test(t) || /\.(png|jpe?g|webp|bmp)$/i.test(n);
  }
  function need(f) { return !window.__dcNoShrink && f && isRaster(f) && f.size > BYTES; }   // pixels are checked after decode

  function decode(f) {
    return new Promise(function (res, rej) {
      var u = URL.createObjectURL(f), im = new Image();
      im.onload = function () { res({ im: im, url: u }); };
      im.onerror = function () { URL.revokeObjectURL(u); rej(new Error('decode')); };
      im.src = u;
    });
  }
  function shrinkFile(f) {
    if (!need(f)) return Promise.resolve(f);
    return decode(f).then(function (d) {
      var w = d.im.naturalWidth, h = d.im.naturalHeight;
      var k = Math.min(1, Math.sqrt(AREA / (w * h)), LONG / Math.max(w, h));
      var c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
      var g = c.getContext('2d'); g.drawImage(d.im, 0, 0, c.width, c.height);
      URL.revokeObjectURL(d.url);
      var alpha = false, px = g.getImageData(0, 0, c.width, c.height).data;
      for (var i = 3; i < px.length; i += 4 * 61) if (px[i] < 250) { alpha = true; break; }
      var mime = alpha ? 'image/png' : 'image/jpeg';
      var enc = function (q) { return new Promise(function (r) { c.toBlob(r, mime, q); }); };
      return enc(Q).then(function (b) { return (b && b.size > RETRY_OVER && !alpha) ? enc(Q2).then(function (b2) { return (b2 && b2.size < b.size) ? b2 : b; }) : b; }).then(function (b) {
        return new Promise(function (res) {
          if (!b || b.size >= f.size) return res(f);                         // never make it bigger
          var name = (f.name || 'image').replace(/\.(png|jpe?g|webp|bmp)$/i, '') + (alpha ? '.png' : '.jpg');
          var out;
          try { out = new File([b], name, { type: mime, lastModified: Date.now() }); } catch (e) { return res(f); }
          out.__dcOptimized = { from: [w, h], to: [c.width, c.height], bytes: f.size };
          res(out);
        });
      });
    }).catch(function () { return f; });
  }
  function mb(n) { return (n / 1048576).toFixed(1) + 'MB'; }
  function tell(f) {
    var o = f && f.__dcOptimized;
    if (!o || typeof window.__dcToast !== 'function') return;
    try { window.__dcToast('Photo optimized for print: ' + mb(o.bytes) + ' → ' + mb(f.size) + (o.from[0] !== o.to[0] ? ' (' + o.from[0] + '×' + o.from[1] + ' → ' + o.to[0] + '×' + o.to[1] + ')' : '')); } catch (e) {}
  }

  /* drag & drop and other scripts: shrink a list, keep order */
  window.__dcShrinkFiles = function (files) {
    var list = Array.prototype.slice.call(files || []);
    return Promise.all(list.map(shrinkFile)).then(function (out) { out.forEach(tell); return out; });
  };
  window.__dcShrinkFile = shrinkFile;

  /* the Image panel's <input type=file>: take the change event first, hand the bundle a smaller file */
  document.addEventListener('change', function (e) {
    var inp = e.target;
    if (!inp || inp.tagName !== 'INPUT' || inp.type !== 'file' || inp.__dcShrunk) return;
    if (/^sr-file/.test(inp.id || '')) return;                                 // Step & Repeat shrinks its own
    if (!inp.files || inp.files.length !== 1 || !need(inp.files[0])) return;
    if (typeof DataTransfer === 'undefined') return;
    var f = inp.files[0];
    e.stopImmediatePropagation(); e.preventDefault();
    var again = function (g) {
      try { if (g !== f) { var dt = new DataTransfer(); dt.items.add(g); inp.files = dt.files; } } catch (x) {}
      inp.__dcShrunk = true;
      try { inp.dispatchEvent(new Event('change', { bubbles: true })); } finally { inp.__dcShrunk = false; }
      tell(g);
    };
    shrinkFile(f).then(again, function () { again(f); });
  }, true);
})();
